import { select as _generateCssSelector, optimize, common } from 'optimal-select';

export function addClass (el, className) {
  if (el.classList) {
    el.classList.add(className);
  } else if (!hasClass(el, className)) {
    el.className += ' ' + className;
  }
}

function hasClass (el, className) {
  if (el.classList) {
    return el.classList.contains(className);
  } else {
    return !!el.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'));
  }
}

export function isFixed (el) {
  while (el && el !== document.body) {
    if (el.style && el.style.position === 'fixed') {
      return true;
    }
    el = el.parentNode;
  }
  return false;
}

export function removeClass (el, className) {
  if (el.classList) {
    el.classList.remove(className);
  } else if (hasClass(el, className)) {
    var reg = new RegExp('(\\s|^)' + className + '(\\s|$)');
    el.className=el.className.replace(reg, ' ');
  }
}

const ignoreDefault = {
  class (name, value, defaultPredicate) {
    return !shouldIncludeCssClass(name, value) || defaultPredicate(name, value);
  },
  attribute (name, value, defaultPredicate) {
    return !shouldIncludeAttribute(name, value) || defaultPredicate(name, value);
  },
  id (name, value, defaultPredicate) {
    return !shouldIncludeId(name, value) || defaultPredicate(name, value);
  }
};

/*
 * The goal of this function is to generate a not-too-strict
 * css selector so that we have a selector that captures
 * "similar" elements.
 */
export const generateGeneralCssSelector = function (element) {
  let singleSelector = generateCssSelector(element);
  let options = {
    ignore: {
      /* jshint ignore:start */
      ...ignoreDefault,
      /* jshint ignore:end */
      attribute (name, value, defaultPredicate) {
        return !shouldIncludeAttribute(name, value, EXCLUDED_ATTRIBUTES.concat('href')) ||
               defaultPredicate(name, value);
      },
      id (id) {
        return true;
      }
    }
  };

  // try css selector without id and href
  let multiSelector = _generateCssSelector(element, options);
  if (document.querySelectorAll(multiSelector).length > document.querySelectorAll(singleSelector).length) {
    return multiSelector;
  }

  // try css selector that's only unique in parent
  options.root = element.parentElement;
  let parentScopeSingleSelector = _generateCssSelector(element, options);
  options = {ignore: ignoreDefault};
  let numElements = document.querySelectorAll(parentScopeSingleSelector).length;
  if (numElements > 1) {
    // limit the selected elements by checking in it's parent's parent and upwards
    // insert parent node tagName to avoid selecting both parent and child nodes if they have the same class name
    let selector = element.parentElement.tagName.toLowerCase() + ' ' + parentScopeSingleSelector;
    let node = element.parentElement.parentElement;

    while (node !== document.body) {
      if (node.tagName.toLowerCase() !== 'tr') {
        let elementsInScope = node.querySelectorAll(selector);
        if (elementsInScope.length > 1 && elementsInScope.length <= numElements) {
          options.root = node;
          return _generateCssSelector(elementsInScope, options);
        }
      }
      selector = node.tagName.toLowerCase() + ' ' + selector;
      node = node.parentElement;
    }
  } else {
    // the css selector gerenates a unique element in it's parent
    let pattern = /:nth-of-(\w+)\(\d\)$/;
    if (pattern.test(parentScopeSingleSelector)) {
      // remove nth-of-xxx selector
      parentScopeSingleSelector = parentScopeSingleSelector.replace(pattern, '');
      let elementsInScope = document.querySelectorAll(parentScopeSingleSelector);
      if (elementsInScope.length > 1) {
        options.root = element.parentElement;
        return optimize(parentScopeSingleSelector, elementsInScope);
      }
    }
    return getSimilarElementSelector(element);
  }
  return multiSelector;
};

/*
 * Attempt to generate a unique css selector that does not use dynamic values
 * so that it's valid if the data changes on the page (think a list of
 * posts on the reddit home page)
 */
export const generateCssSelector = function (element, options, shadowRoot) {
  options = options || { ignore: ignoreDefault };
  return _generateCssSelector(element, options, shadowRoot);
};

function getCommonClassName (element) {
  let elements = [].slice.call(element.parentElement.childNodes).filter(node => node.nodeType === Node.ELEMENT_NODE);
  let commentProperties = common.getCommonProperties(elements);
  if (commentProperties.classes && commentProperties.classes.length) {
    return getValidClassName(commentProperties.classes.join(' '));
  }
  return '';
}

function getElementValidClassName (elem) {
  let className;
  if (elem.tagName.toLowerCase() === 'svg') {
    className = elem.className.baseVal || elem.getAttribute('class') || '';
  } else {
    className = elem.className || '';
  }

  return getValidClassName(className);
}

function getSimilarElementSelector (target) {
  let ELEMENT_NODE = 1;
  if (!target || !target.parentNode || target === document ||
      target === document.body.parentNode || target.nodeType !== ELEMENT_NODE) {
    return '';
  }

  if (target === document.body) {
    return 'body';
  }

  var targetElementList = [];
  var curEl = target.parentNode;
  while (curEl && curEl !== document.body) {
    targetElementList.push(curEl);
    curEl = curEl.parentNode;
  }

  let selectorList = [];
  // add the selector for the parent nodes;
  for (let i = targetElementList.length - 1; i >= 0; i--) {
    let el = targetElementList[i];
    let tagName = el.tagName.toLowerCase();
    let className = getElementValidClassName(el);
    let id = getValidId(el);
    let classes = className.length > 0 ? '.'.concat(className.replace(/\s+/g, '.')) : tagName;
    let selector = id !== '' ? '#' + id : classes;
    selectorList.push(selector);
  }

  // add the selector for the target element
  let className = getCommonClassName(target);
  let tagName = target.tagName.toLowerCase();
  selectorList.push(className.length > 0 ? '.'.concat(className.replace(/\s+/g, '.')) : tagName);

  let selector = selectorList.join(' ');
  let elements = document.querySelectorAll(selector);
  return optimize(selector, elements);
}

function getValidClassName (className) {
  let classNames = className.trim().split(' ');
  for (let i = classNames.length - 1; i >= 0; i--) {
    let cls = classNames[i];
    if (name.indexOf('ng-valid') === 0 || name.indexOf('ng-invalid') ===0 || NG_CLASS_NAMES.indexOf(name) >= 0) {
      classNames.pop(i);
    }
    if (EXCLUDED_CLASS_NAMES.indexOf(cls) !== -1) {
      classNames.pop(i);
    }
    // checkout yahoo's css selectors for why we want to exclude classes with parens
    if (cls.indexOf('(') >= 0) {
      classNames.pop(i);
    }
  }
  return classNames.join(' ');
}

function getValidId (elem) {
  let id = elem.id || '';
  if (shouldIncludeId('id', id)) {
    return id;
  }
  return '';
}

const NG_CLASS_NAMES = ['ng-pristine', 'ng-dirty', 'ng-touched', 'ng-untouched', 'ng-pending'];
const EXCLUDED_CLASS_NAMES = [
  'odd', 'even', 'active', 'selected', 'highlight', 'required', 'req', 'first', 'last', 'hover', 'mouseover'
];
function shouldIncludeCssClass (name, value) {
  if (!value || !value.trim()) {
    return false;
  }

  if (value.indexOf('ng-valid') === 0 || value.indexOf('ng-invalid') === 0 || NG_CLASS_NAMES.indexOf(value) >= 0) {
    return false;
  }
  if (EXCLUDED_CLASS_NAMES.indexOf(value) > -1) {
    return false;
  }
  // checkout yahoo's css selectors for why we want to exclude classes with parens
  if (value.indexOf('(') > -1) {
    return false;
  }

  return true;
}

const EXCLUDED_ID_REGEXES = [/^ember[\d]+/, /^ext-gen[\d]+/, /^ng-.+/, /^yui_[_\d]+/];
function shouldIncludeId (name, id = '') {
  let matches = EXCLUDED_ID_REGEXES.filter(function (pattern) {
    return id.match(pattern);
  });
  if (matches.length > 0) {
    return false;
  }
  // don't use id if it looks like a generated id
  if (/[0-9]{3}|^\d+$/.test(id)) {
    return false;
  }

  return true;
}

const EXCLUDED_ATTRIBUTES = [
  'class', 'data-ember-action', 'data-react-checksum', 'data-reactid', 'id', 'maxlength', 'onclick', 'onsubmit', 'style'
];
function shouldIncludeAttribute (name, val, excludedAttributes = EXCLUDED_ATTRIBUTES) {
  if (excludedAttributes.indexOf(name) >= 0) {
    return false;
  }
  switch (name) {
    case 'href':
      // don't use href if it looks dynamic
      return /[0-9]{3}|\?/.test(val);
    default:
      // don't use any other attributes (such as data-) as they tend to be dynamic
      return true;
  }
}



/** WEBPACK FOOTER **
 ** ./src/utils/css.js
 **/